import { useRef, useState } from "react";
import clsx from "clsx";

import { actionZoomToFit } from "../actions/actionCanvas";
import { useOutsideClick } from "../hooks/useOutsideClick";
import { t } from "../i18n";
import { getNormalizedZoom } from "../scene";
import { getStateForZoom } from "../scene/zoom";

import { Island } from "./Island";

import "./MobileZoomBar.scss";

import type { ActionManager } from "../actions/manager";
import type { AppState, UIAppState } from "../types";

const ZOOM_PRESETS = [50, 75, 100, 150, 200] as const;

type MobileZoomBarProps = {
  appState: UIAppState;
  setAppState: React.Component<any, AppState>["setState"];
  actionManager: ActionManager;
};

/**
 * PizzarWeb: compact zoom pill for the mobile bottom bar. Desktop shows the
 * footer ZoomActions, but mobile had no zoom indicator at all. Tapping the
 * percentage opens a preset menu (50–200% + zoom-to-fit).
 */
export const MobileZoomBar = ({
  appState,
  setAppState,
  actionManager,
}: MobileZoomBarProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useOutsideClick(containerRef, () => setIsMenuOpen(false));

  const currentPercent = Math.round(appState.zoom.value * 100);

  const setZoom = (nextValue: number) => {
    setAppState((prev: AppState) => ({
      ...getStateForZoom(
        {
          viewportX: prev.width / 2 + prev.offsetLeft,
          viewportY: prev.height / 2 + prev.offsetTop,
          nextZoom: getNormalizedZoom(nextValue),
        },
        prev,
      ),
      userToFollow: null,
    }));
    setIsMenuOpen(false);
  };

  return (
    <div className="mobile-zoom-bar" ref={containerRef}>
      {isMenuOpen && (
        <Island className="mobile-zoom-bar__menu">
          {ZOOM_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={clsx("mobile-zoom-bar__menu-item", {
                "mobile-zoom-bar__menu-item--active": currentPercent === preset,
              })}
              onClick={() => setZoom(preset / 100)}
            >
              {preset}%
            </button>
          ))}
          <button
            type="button"
            className="mobile-zoom-bar__menu-item"
            onClick={() => {
              actionManager.executeAction(actionZoomToFit);
              setIsMenuOpen(false);
            }}
          >
            {t("labels.zoomToFit")}
          </button>
        </Island>
      )}
      <Island className="mobile-zoom-bar__controls">
        {actionManager.renderAction("zoomOut")}
        <button
          type="button"
          className="mobile-zoom-bar__value"
          title={t("buttons.zoomMenu")}
          aria-label={t("buttons.zoomMenu")}
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          {currentPercent}%
        </button>
        {actionManager.renderAction("zoomIn")}
      </Island>
    </div>
  );
};
